/**
 * Auth Store — Zustand State Management fuer die OIDC-Authentifizierung (Authentik).
 * Speichert User-Profil und Rollen aus dem Backend (/api/v1/auth/me).
 * oidc-client-ts verwaltet Tokens — dieses Store hat keine Passwort-Logik.
 */

import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"

// =============================================================================
// TYPES
// =============================================================================

export interface Permission {
  id: string
  name: string
  description: string | null
  resource: string
  action: string
}

export interface Role {
  id: string
  name: string
  description: string | null
  isSystem: boolean
  permissions: Permission[]
}

export interface User {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  logtoUserId?: string | null // OIDC sub (API-Feldname historisch)
  lastLogin: string | null
  createdAt: string
  updatedAt: string
  roles: Role[]
}

interface AuthState {
  // State
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  tenantId: string | null

  // Actions
  setUser: (user: User) => void
  setAuthenticated: (auth: boolean) => void
  setTenantId: (id: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Permission Helpers
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  isAdmin: () => boolean
}

// =============================================================================
// STORE
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      tenantId: null,

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
        }),

      setAuthenticated: (auth) =>
        set({
          isAuthenticated: auth,
        }),

      setTenantId: (id) =>
        set({
          tenantId: id,
        }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          tenantId: null,
        }),

      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      // Permission Helpers
      hasPermission: (permission) => {
        const { user } = get()
        if (!user || !user.roles) return false
        if (user.roles.some((r) => r.name === "admin" || r.name === "superadmin")) return true
        return user.roles.some((role) =>
          role.permissions?.some((p) => p.name === permission)
        )
      },

      hasRole: (roleName) => {
        const { user } = get()
        if (!user || !user.roles) return false
        return user.roles.some((r) => r.name === roleName)
      },

      isAdmin: () => {
        const { user } = get()
        if (!user || !user.roles) return false
        return user.roles.some((r) => r.name === "admin" || r.name === "superadmin")
      },
    }),
    {
      name: "factoryerp-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tenantId: state.tenantId,
      }),
    }
  )
)

// =============================================================================
// SELECTORS
// =============================================================================

export const selectUser = (state: AuthState) => state.user
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated
export const selectIsLoading = (state: AuthState) => state.isLoading
